// Status / quota / replay / analysis / receipt panels — pure render
// functions of the SessionStore projection. Interactions are callbacks
// supplied by the App shell.

import { useRef, useState } from "react";

import type { SessionStore } from "../core/store.js";
import { ACTION_KINDS, type ActionKind } from "../core/codec.js";
import { parseSgfImport, SgfError, type SgfMove } from "../core/sgf.js";
import { actionLabel, playerLabel } from "./Board.js";
import { readSgfFile } from "../core/sgf-file.js";
import { Play, Square, ArrowRight, SkipBack, SkipForward, ChevronLeft, ChevronRight } from "lucide-react";
import type { QuotaVector } from "../core/wire.js";

export function StatusBar(props: { store: SessionStore }) {
  const { store } = props;
  // The position fields track the replay cursor (view), matching the board.
  const view = store.view;
  return (
    <div className="status-bar" data-phase={store.phase}>
      <span className="conn">{store.phase}</span>
      {store.baselinePending && <span className="pending">等待基线快照…</span>}
      {view !== null && (
        <span className="rev">
          rev {view.revision} · 着手方 {playerLabel(view.state.actor)} · 阶段 {view.state.phase}
          {store.isReplay && " (回放投影)"}
        </span>
      )}
      {store.broken !== null && <span className="fatal">协议失败关闭:{store.broken}</span>}
      {store.sessionError !== null && (
        <span className="fatal">
          会话故障 {store.sessionError.errorClass}:{store.sessionError.detail}
        </span>
      )}
      {store.lastTransportError !== null && (
        <span className="transport">传输 {store.lastTransportError.errorClass}</span>
      )}
    </div>
  );
}

export function QuotaBar(props: { store: SessionStore }) {
  // Quotas/terminal/score are position fields: follow the replay cursor.
  const view = props.store.view;
  if (view === null) return null;
  const { state } = view;
  const actor = state.actor === "NONE" ? null : state.actor;
  const quotas = actor === null ? null : state.quotas.remaining[actor];
  const initial = actor === null ? null : state.quotas.initial[actor];
  return (
    <div className="quota-bar">
      {quotas !== null && initial !== null && (
        <span>
          配额({playerLabel(actor!)}): IMMORTAL {quotas.IMMORTAL}/{initial.IMMORTAL} ·
          DOUBLE_START {quotas.DOUBLE_START}/{initial.DOUBLE_START} ·
          EIGHTWAY {quotas.EIGHTWAY}/{initial.EIGHTWAY}
        </span>
      )}
      {state.pendingDouble !== null && (
        <span className="pending-double">
          待续 DOUBLE_START:{playerLabel(state.pendingDouble.owner)} #{state.pendingDouble.specialLink}
        </span>
      )}
      {state.terminalState !== null && (
        <span className="terminal">
          终局 {state.terminalState.reason} — 胜方 {playerLabel(state.terminalState.winner)}
        </span>
      )}
      {state.score.isScored && (
        <span className="score">
          比分 黑 {state.score.blackScoreNumerator} : 白 {state.score.whiteScoreNumerator}
          (子 {state.score.blackStones}/{state.score.whiteStones} 地 {state.score.blackTerritory}/{state.score.whiteTerritory})
        </span>
      )}
    </div>
  );
}

export function KindSelector(props: {
  selected: ActionKind;
  onSelect: (kind: ActionKind) => void;
  disabled: boolean;
  remaining?: QuotaVector | undefined;
}) {
  return (
    <div className="kind-selector">
      {ACTION_KINDS.map((kind) => (
        <button
          key={kind}
          aria-label={kind}
          title={kind}
          className={kind === props.selected ? "selected" : ""}
          disabled={props.disabled || (kind !== "NORMAL" && props.remaining?.[kind] === 0)}
          onClick={() => props.onSelect(kind)}
        >
          <span>{{ NORMAL: "普通", IMMORTAL: "不死", DOUBLE_START: "连走", EIGHTWAY: "八向" }[kind]}</span>
          <small>{{ NORMAL: "N", IMMORTAL: "I", DOUBLE_START: "D", EIGHTWAY: "E" }[kind]}</small>
        </button>
      ))}
    </div>
  );
}

export function ReplayBar(props: { store: SessionStore }) {
  const { store } = props;
  if (store.history.length === 0) return null;
  return (
    <div className="replay-bar" data-replay={store.isReplay ? "true" : "false"}>
      <button onClick={() => store.setCursor(0)} title="回到开局" aria-label="回到开局"><SkipBack size={18} /></button>
      <button onClick={() => store.setCursor(store.cursor - 1)} title="后退一手" aria-label="后退一手"><ChevronLeft size={18} /></button>
      <span>
        {store.cursor} / {store.history.length - 1}
        {store.isReplay && " (回放中)"}
      </span>
      <button onClick={() => store.setCursor(store.cursor + 1)} title="前进一手" aria-label="前进一手"><ChevronRight size={18} /></button>
      <button onClick={() => store.cursorLatest()} title="回到最新" aria-label="回到最新"><SkipForward size={18} /></button>
    </div>
  );
}

export function AnalysisPanel(props: {
  store: SessionStore;
  canPlayChosen: boolean;
  onPlayChosen: () => void;
}) {
  const { store } = props;
  const analysis = store.analysis;
  const connected =
    store.broken === null &&
    (store.phase === "live" || store.phase === "syncing" || store.phase === "recovering");
  return (
    <div className="analysis-panel">
      <div className="analysis-controls">
        <button onClick={props.onPlayChosen} disabled={!props.canPlayChosen}><ArrowRight size={15} />采用推荐</button>
        {store.analysisError !== null && (
          <span className="analysis-error">分析错误:{store.analysisError.errorClass}</span>
        )}
      </div>
      {analysis !== null && (
        <table>
          <thead>
            <tr>
              <th>着手</th>
              <th>访问量</th>
              <th title="按 (Q+1)/2 映射，无结果计半">胜率估计</th>
              <th>Q</th>
              <th>目差</th>
              <th>PV</th>
            </tr>
          </thead>
          <tbody>
            {analysis.moves.map((row) => (
              <tr key={row.action.actionId}>
                <td>{actionLabel(row.action.actionId)}</td>
                <td>{row.visits}</td>
                <td>{((row.qActorMicro / 1000000 + 1) * 50).toFixed(1)}%</td>
                <td>{(row.qActorMicro / 1000000).toFixed(3)}</td>
                <td title="后端未提供目差">—</td>
                <td>{row.pv.map((a) => actionLabel(a.actionId)).join(" ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {analysis !== null && (
        <div className="analysis-meta">
          basis rev {analysis.basisRevision} · playouts {analysis.playoutsCompleted} ·{" "}
          {analysis.elapsedMillis}ms
          {analysis.chosenAction !== null && ` · 选择 ${actionLabel(analysis.chosenAction.actionId)}`}
        </div>
      )}
    </div>
  );
}

export function ReceiptLog(props: { store: SessionStore; max?: number }) {
  const entries = [...props.store.receipts.values()].slice(-(props.max ?? 8));
  const pending = [...props.store.pending.values()];
  if (entries.length === 0 && pending.length === 0) return null;
  return (
    <div className="receipt-log">
      {pending.map((p) => (
        <div key={p.commandId} className={`pending ${p.status}`}>
          {p.commandId} — {p.status === "unknown" ? "状态未知,核对中" : "已提交,等待回执"}
        </div>
      ))}
      {entries.map((receipt) => (
        <div key={receipt.commandId} className={receipt.accepted ? "accepted" : "rejected"}>
          {receipt.commandId} —{" "}
          {receipt.accepted
            ? `已接受${receipt.redelivered ? " (重投递)" : ""}`
            : receipt.transportErrorClass !== null
              ? `传输拒绝 ${receipt.transportErrorClass}`
              : `拒绝 ${receipt.error!.category}/${receipt.error!.code}`}
        </div>
      ))}
    </div>
  );
}

// SGF export/import panel (M6-D4). The export is the gateway's
// collapse-sgf-loss-v1 text; the import parses untrusted SGF into
// mainline intents that replay as NORMAL/PASS commands against a FRESH
// session — the loss declaration is shown before the user confirms.
export function SgfPanel(props: {
  store: SessionStore;
  onImport: (moves: SgfMove[]) => void;
}) {
  const { store } = props;
  const [importText, setImportText] = useState("");
  const [parsed, setParsed] = useState<SgfMove[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const importGeneration = useRef(0);

  const exported = store.lastExport;
  const onDownload = () => {
    if (exported === null) return;
    const blob = new Blob([exported.sgf], { type: "application/x-go-sgf" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `collapse-${store.sessionId}-r${store.tip?.revision ?? 0}.sgf`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const onParse = () => {
    try {
      const result = parseSgfImport(importText);
      setParsed(result.moves);
      setParseError(
        result.moves.length === 0 ? "SGF 不含任何着手" : null,
      );
      if (result.moves.length === 0) setParsed(null);
    } catch (error) {
      setParsed(null);
      setParseError(error instanceof SgfError ? error.message : String(error));
    }
  };

  return (
    <div className="sgf-panel">
      <h2>SGF</h2>
      <div className="sgf-export">
        {exported === null ? (
          <span className="dim">尚无导出（使用上方"导出 SGF")</span>
        ) : (
          <>
            <textarea readOnly={true} value={exported.sgf} rows={4} />
            <button onClick={onDownload}>下载 {exported.format}</button>
          </>
        )}
      </div>
      <div className="sgf-import">
        <label className="sgf-file">
          <span>选择棋谱</span>
          <input type="file" accept=".sgf,application/x-go-sgf,text/plain" aria-label="导入 SGF 文件"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file === undefined) return;
              const generation = ++importGeneration.current;
              setImportText("");
              setParsed(null);
              setParseError(null);
              try {
                const text = await readSgfFile(file);
                if (generation === importGeneration.current) setImportText(text);
              } catch (error) {
                if (generation === importGeneration.current)
                  setParseError(error instanceof Error ? error.message : String(error));
              }
            }} />
        </label>
        <textarea
          value={importText}
          rows={4}
          placeholder="粘贴 SGF(仅主线,FF[4]/GM[1]/SZ[19])"
          onChange={(event) => {
            importGeneration.current++;
            setImportText(event.target.value);
            setParsed(null);
            setParseError(null);
          }}
        />
        <button onClick={onParse} disabled={importText.trim() === ""}>解析</button>
        {parseError !== null && <span className="fatal">{parseError}</span>}
        {parsed !== null && (
          <div className="sgf-import-preview">
            <span>{parsed.length} 手可见主线</span>
            <span className="loss-declaration">
              有损重放:kind 信息已按 collapse-sgf-loss-v1 丢失,全部着手将以
              NORMAL/PASS 意图重放到全新对局;特殊招法可能被权威端拒绝,配额、
              结算与日志边界不恢复。
            </span>
            <button onClick={() => props.onImport(parsed)}>重放到新对局</button>
          </div>
        )}
        {store.importStatus !== null && <span className="import-status">{store.importStatus}</span>}
      </div>
    </div>
  );
}
